import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faFilePdf, faImage, faTrash } from '@fortawesome/free-solid-svg-icons';
import { dubaiDate } from '../../../utils/dubaiTime';
import './clientDetails.css';

export const DOC_SLOTS = [
    { type: 'trade_license', label: 'Trade License' },
    { type: 'vat_certificate', label: 'TRN / VAT Certificate' },
];

const acceptFile = (f) => f && (f.type.startsWith('image/') || f.type === 'application/pdf');

// Upload staged files ({ trade_license: File|null, vat_certificate: File|null })
// to a client after it exists (used by the Add Client flow post-create).
export async function uploadClientDocuments(clientId, files) {
    for (const { type } of DOC_SLOTS) {
        const file = files?.[type];
        if (!file) continue;
        const presign = await axios.post(`/api/clients/${clientId}/documents/upload-url`, {
            fileName: file.name, fileType: file.type,
        });
        const { url, fileKey, fileUrl } = presign.data;
        await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' }, body: file });
        await axios.post(`/api/clients/${clientId}/documents`, { file_url: fileUrl, file_key: fileKey, type });
    }
}

// Documents for an EXISTING client. Uploading a missing document is always
// available (easy). Replacing/removing an existing one is gated behind
// `manage` (i.e. only on the Edit Client page) to avoid accidental loss.
export default function ClientDocuments({ clientId, manage = false }) {
    const [docs, setDocs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyType, setBusyType] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`/api/clients/${clientId}/documents`);
            setDocs(Array.isArray(res.data) ? res.data : []);
        } catch (e) {
            console.error('Error loading client documents:', e);
            setDocs([]);
        } finally {
            setLoading(false);
        }
    }, [clientId]);

    useEffect(() => { load(); }, [load]);

    const uploadTo = async (type, file) => {
        if (!file) return;
        if (!acceptFile(file)) { window.alert('Only image or PDF files are allowed.'); return; }
        setBusyType(type);
        try {
            const presign = await axios.post(`/api/clients/${clientId}/documents/upload-url`, {
                fileName: file.name, fileType: file.type,
            });
            const { url, fileKey, fileUrl } = presign.data;
            await fetch(url, { method: 'PUT', headers: { 'Content-Type': file.type, 'x-amz-acl': 'public-read' }, body: file });
            await axios.post(`/api/clients/${clientId}/documents`, { file_url: fileUrl, file_key: fileKey, type });
            await load();
        } catch (err) {
            console.error('Error uploading client document:', err);
            window.alert('Failed to upload the document. Please try again.');
        } finally {
            setBusyType(null);
        }
    };

    // Admin verification (2026-08-07): every client-uploaded document
    // must be CHECKED. Verify stamps it; Reject re-locks the client's
    // compliance gate and notifies them to re-upload.
    const verify = async (docId) => {
        try {
            await axios.post(`/api/clients/${clientId}/documents/${docId}/verify`);
            await load();
        } catch (e) {
            console.error('Error verifying document:', e);
            window.alert('Could not verify the document. Please try again.');
        }
    };

    const reject = async (docId) => {
        const note = window.prompt('Why is this document being rejected? (shown to the client)', '');
        if (note === null) return; // cancelled
        try {
            await axios.post(`/api/clients/${clientId}/documents/${docId}/reject`, { note });
            await load();
        } catch (e) {
            console.error('Error rejecting document:', e);
            window.alert('Could not reject the document. Please try again.');
        }
    };

    const remove = async (docId) => {
        if (!window.confirm('Delete this document?')) return;
        try {
            await axios.delete(`/api/clients/${clientId}/documents/${docId}`);
            setDocs((prev) => prev.filter((d) => d.id !== docId));
        } catch (e) {
            console.error('Error deleting client document:', e);
        }
    };

    const docFor = (type) => docs.find((d) => d.type === type) || null;

    return (
        <div className="client-documents-section">
            <div className="client-documents-head"><h3>Documents</h3></div>
            {loading ? (
                <div className="client-documents-empty">Loading…</div>
            ) : (
                <div className="client-documents-slots">
                    {DOC_SLOTS.map((slot) => (
                        <ExistingDocSlot
                            key={slot.type}
                            label={slot.label}
                            doc={docFor(slot.type)}
                            manage={manage}
                            busy={busyType === slot.type}
                            onUpload={(f) => uploadTo(slot.type, f)}
                            onRemove={remove}
                            onVerify={verify}
                            onReject={reject}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

function ExistingDocSlot({ label, doc, manage, busy, onUpload, onRemove, onVerify, onReject }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const isPdf = doc && /\.pdf($|\?)/i.test(doc.file_url);
    const handle = (list) => { const f = Array.from(list || [])[0]; if (f) onUpload(f); };
    // Verification state: rejected outranks verified; neither = Unverified.
    const state = doc ? (doc.rejected_at ? 'rejected' : (doc.verified_at ? 'verified' : 'unverified')) : null;

    return (
        <div className="admin-docslot">
            <div className="admin-docslot__label">{label}</div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => { handle(e.target.files); e.target.value = ''; }}
            />
            {doc ? (
                <div className="admin-docslot__file">
                    <a href={doc.file_url} target="_blank" rel="noopener noreferrer" className="admin-docslot__open">
                        <FontAwesomeIcon icon={isPdf ? faFilePdf : faImage} className="admin-docslot__icon" />
                        <div>
                            <div className="admin-docslot__name">{isPdf ? 'PDF document' : 'Image'}</div>
                            <div className="admin-docslot__date">
                                {doc.uploaded_at ? dubaiDate(doc.uploaded_at) : 'View'}
                                {doc.uploaded_by_name?.trim() ? ` · by ${doc.uploaded_by_name.trim()}` : ''}
                            </div>
                        </div>
                    </a>
                    {state === 'verified' && (
                        <span className="admin-docslot__chip admin-docslot__chip--ok" title={doc.verified_by_name?.trim() ? `Verified by ${doc.verified_by_name.trim()}` : 'Verified'}>
                            Verified ✓
                        </span>
                    )}
                    {state === 'rejected' && (
                        <span className="admin-docslot__chip admin-docslot__chip--bad" title={doc.review_note || 'Rejected'}>
                            Rejected
                        </span>
                    )}
                    {state === 'unverified' && (
                        <>
                            <span className="admin-docslot__chip admin-docslot__chip--pending">Unverified</span>
                            <button type="button" className="admin-docslot__verify" onClick={() => onVerify(doc.id)}>
                                Verify
                            </button>
                            <button type="button" className="admin-docslot__rejectBtn" onClick={() => onReject(doc.id)}>
                                Reject
                            </button>
                        </>
                    )}
                    {manage && (
                        <>
                            <button type="button" className="admin-docslot__replace" onClick={() => inputRef.current?.click()} disabled={busy}>
                                {busy ? 'Uploading…' : 'Replace'}
                            </button>
                            <button type="button" className="admin-docslot__del" onClick={() => onRemove(doc.id)} title="Delete">
                                <FontAwesomeIcon icon={faTrash} />
                            </button>
                        </>
                    )}
                </div>
            ) : (
                <button
                    type="button"
                    className={`admin-docslot__drop ${dragOver ? 'is-drag' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files); }}
                    disabled={busy}
                >
                    <FontAwesomeIcon icon={faUpload} className="admin-docslot__dropIcon" />
                    <span className="admin-docslot__dropText">{busy ? 'Uploading…' : 'Click or drop a file'}</span>
                    <span className="admin-docslot__dropHint">Image or PDF</span>
                </button>
            )}
        </div>
    );
}

// Staged document slots for the CREATE flow (no client id yet). Controlled:
// the parent holds `files` = { trade_license: File|null, vat_certificate: File|null }
// and uploads them with uploadClientDocuments() after the client is created.
export function StagedClientDocuments({ files, setFiles }) {
    const setSlot = (type, file) => setFiles((p) => ({ ...p, [type]: file }));
    return (
        <div className="client-documents-slots">
            {DOC_SLOTS.map((slot) => (
                <StagedDocSlot
                    key={slot.type}
                    label={slot.label}
                    file={files?.[slot.type] || null}
                    onPick={(f) => setSlot(slot.type, f)}
                    onClear={() => setSlot(slot.type, null)}
                />
            ))}
        </div>
    );
}

function StagedDocSlot({ label, file, onPick, onClear }) {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);
    const handle = (list) => {
        const f = Array.from(list || [])[0];
        if (!f) return;
        if (!acceptFile(f)) { window.alert('Only image or PDF files are allowed.'); return; }
        onPick(f);
    };
    return (
        <div className="admin-docslot">
            <div className="admin-docslot__label">{label}</div>
            <input
                ref={inputRef}
                type="file"
                accept="image/*,application/pdf"
                style={{ display: 'none' }}
                onChange={(e) => { handle(e.target.files); e.target.value = ''; }}
            />
            {file ? (
                <div className="admin-docslot__file">
                    <div className="admin-docslot__open">
                        <FontAwesomeIcon icon={file.type === 'application/pdf' ? faFilePdf : faImage} className="admin-docslot__icon" />
                        <div>
                            <div className="admin-docslot__name">{file.name}</div>
                            <div className="admin-docslot__date">Ready to upload</div>
                        </div>
                    </div>
                    <button type="button" className="admin-docslot__replace" onClick={() => inputRef.current?.click()}>Replace</button>
                    <button type="button" className="admin-docslot__del" onClick={onClear} title="Remove">
                        <FontAwesomeIcon icon={faTrash} />
                    </button>
                </div>
            ) : (
                <button
                    type="button"
                    className={`admin-docslot__drop ${dragOver ? 'is-drag' : ''}`}
                    onClick={() => inputRef.current?.click()}
                    onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                    onDragLeave={() => setDragOver(false)}
                    onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files); }}
                >
                    <FontAwesomeIcon icon={faUpload} className="admin-docslot__dropIcon" />
                    <span className="admin-docslot__dropText">Click or drop a file</span>
                    <span className="admin-docslot__dropHint">Image or PDF</span>
                </button>
            )}
        </div>
    );
}
